import type { Me } from '@greco-gls/shared';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

/**
 * Global client state (spec §9.4). Server data does **not** live here — that is TanStack Query's
 * job. What is here is the state the URL and the cache cannot hold: who is signed in, whether the
 * app is allowed to render at all, and the unsaved edits sitting in the grid.
 */

/** Where the app is in the admission sequence (port of MGA's `accessStateAtom`). */
export type AccessState = 'loading' | 'ready' | 'unauthenticated' | 'not-registered' | 'error';

export const accessStateAtom = atom<AccessState>('loading');
export const authUserAtom = atom<Me | null>(null);
export const accessErrorAtom = atom<string | null>(null);

export const isAdminAtom = atom((get) => {
  const me = get(authUserAtom);
  return Boolean(me?.isAdmin ?? me?.isSuperadmin);
});

/** Only a superadmin runs the Phrase sync: it writes into every project at once (ADR 0008). */
export const isSuperadminAtom = atom((get) => Boolean(get(authUserAtom)?.isSuperadmin));

/** Project the editor is pointed at; the switcher writes it, the URL usually wins. */
export const currentProjectAtom = atomWithStorage<string | null>('gls.currentProject', null);

/**
 * Locale columns the grid shows. Persisted because it is a workspace preference, not navigation:
 * a translator who works on two languages should not re-pick them every morning.
 */
export const visibleLocalesAtom = atomWithStorage<string[]>('gls.visibleLocales', []);

/**
 * What the notification bell has marked read (`AttentionReadMarks`). Per browser, like the
 * workspace preferences above: a mark only quiets the badge — the work stays listed until it is
 * done — so losing it on another machine costs one click.
 */
export const attentionReadMarksAtom = atomWithStorage<Record<string, number>>(
  'gls.attentionRead',
  {},
);

/**
 * Light or dark, or whatever the operating system says. Per browser like the other workspace
 * preferences; `system` by default, so nobody has to find the switch to get the mode they expect.
 */
export type ColorModePreference = 'system' | 'light' | 'dark';

export const colorModeAtom = atomWithStorage<ColorModePreference>('gls.colorMode', 'system');

/** How many locale columns the grid can show before it stops being readable (spec §9.3). */
export const MAX_VISIBLE_LOCALES = 6;

/** Identity of one editable cell. The grid keys everything on this, never on a row index (§9.3A). */
export interface CellId {
  keyId: number;
  locale: string;
  pluralForm: string;
  context: string;
}

export const cellKey = ({ keyId, locale, pluralForm, context }: CellId): string =>
  `${keyId}|${locale}|${pluralForm}|${context}`;

export interface PendingEdit {
  cell: CellId;
  value: string;
  /** The revision the value was read at; the API refuses the write if it has moved (§7.6). */
  revision: number;
}

/**
 * Unsaved cell edits, keyed by `cellKey`. Held outside the query cache on purpose: a refetch must
 * not be able to discard something the user typed (§9.3A).
 */
export const pendingEditsAtom = atom<Map<string, PendingEdit>>(new Map());

export const pendingEditCountAtom = atom((get) => get(pendingEditsAtom).size);

/** Rows kept in the list although they no longer match the filter, because they are being edited. */
export const pinnedKeyIdsAtom = atom<Set<number>>(new Set<number>());

export const setPendingEditAtom = atom(null, (get, set, edit: PendingEdit) => {
  const next = new Map(get(pendingEditsAtom));
  next.set(cellKey(edit.cell), edit);
  set(pendingEditsAtom, next);

  const pinned = get(pinnedKeyIdsAtom);
  if (!pinned.has(edit.cell.keyId)) {
    // The row is being worked on: it stays visible even if the edit makes it fail the filter.
    set(pinnedKeyIdsAtom, new Set(pinned).add(edit.cell.keyId));
  }
});

export const clearPendingEditAtom = atom(null, (get, set, cell: CellId) => {
  const next = new Map(get(pendingEditsAtom));
  next.delete(cellKey(cell));
  set(pendingEditsAtom, next);
});

export const clearPendingEditsAtom = atom(null, (_get, set, keyIds?: number[]) => {
  if (!keyIds) {
    set(pendingEditsAtom, new Map());
    set(pinnedKeyIdsAtom, new Set<number>());
    return;
  }
  set(pendingEditsAtom, (current) => {
    const next = new Map(current);
    for (const [key, edit] of current) {
      if (keyIds.includes(edit.cell.keyId)) next.delete(key);
    }
    return next;
  });
});

export interface Toast {
  id: number;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

export const toastsAtom = atom<Toast[]>([]);

let toastId = 0;

export const pushToastAtom = atom(
  null,
  (get, set, toast: Omit<Toast, 'id'>) => {
    toastId += 1;
    set(toastsAtom, [...get(toastsAtom), { ...toast, id: toastId }]);
  },
);

export const dismissToastAtom = atom(null, (get, set, id: number) => {
  set(
    toastsAtom,
    get(toastsAtom).filter((toast) => toast.id !== id),
  );
});
